/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import React, { useEffect } from "react"

interface ErrorModalProps {
  readonly show: boolean
  readonly title: string
  readonly errorMessages: string | readonly string[]
  readonly onClose: () => void
}

const ErrorModal: React.FC<ErrorModalProps> = ({ show, title, errorMessages, onClose }) => {
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && show) {
        onClose()
      }
    }

    window.addEventListener("keydown", handleEscape)
    return () => {
      window.removeEventListener("keydown", handleEscape)
    }
  }, [show, onClose])

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.classList.add("modal-open")
      document.body.style.overflow = "hidden"
      document.body.style.paddingRight = "15px" // Compensate for scrollbar
    } else {
      document.body.classList.remove("modal-open")
      document.body.style.overflow = ""
      document.body.style.paddingRight = ""
    }

    return () => {
      document.body.classList.remove("modal-open")
      document.body.style.overflow = ""
      document.body.style.paddingRight = ""
    }
  }, [show])

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ opacity: 0.5 }}
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            onClose()
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="errorModalLabel"
        aria-hidden="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content rounded-3 shadow overflow-hidden">
            <div
              className="modal-header border-bottom-0 text-white"
              style={{ backgroundColor: "#dc7676" }} /* Softer red than Bootstrap's danger */
            >
              <div className="d-flex align-items-center">
                <i className="fas fa-exclamation-circle me-2"></i>
                <h5 className="modal-title mb-0" id="errorModalLabel">
                  {title}
                </h5>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body p-4">
              {typeof errorMessages === "string" ? (
                <p className="fs-5">{errorMessages}</p>
              ) : (
                <ul className="mb-0 fs-5">
                  {errorMessages.map((error, index) => (
                    <li key={index} className="mb-2">
                      {error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-footer border-top-0">
              <button
                type="button"
                className="btn rounded-3"
                style={{ backgroundColor: "#dc7676", color: "white" }}
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ErrorModal
